/**
 * usePipelineController — single source of truth for the Monthly Pipeline.
 *
 * Owned by FinancialsDashboard (NOT the Pipeline tab) so the tab-label status
 * dot stays live while the user is on other tabs. The selected month lives in
 * the `?month=` search param — pipeline, upload metadata, and every action all
 * derive from it (no separate pinned "upload month").
 *
 * Polling: 10s while any stage is running, 60s heartbeat otherwise (someone
 * else may confirm a step), plus a refetch when the tab becomes visible.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PipelineRun,
  GamReportUploadMeta,
  GamReportUploadResult,
  PayoutTarget,
  PipelineRerunStep,
  getPipelineStatus,
  getGamReportUpload,
  removeGamReportSource,
  GamUploadRequiresForceError,
  getPipelineHistory,
  getProcessingRun,
  startPipeline,
  cancelPipelineProcessing,
  discardPipeline,
  confirmCmUpload,
  confirmCmLock,
  triggerPipelinePayouts,
  rerunPipelineStep,
  runValidation as runValidationApi,
  setPipelineMode,
} from '../../../services/adminApi';
import { derivePipelinePhase, getExpectedCycleMonth, PipelinePhase } from './pipelineUtils';

const RUNNING_POLL_MS = 10_000;
const IDLE_POLL_MS = 60_000;

/**
 * Post-processing totals from the month's completed DS run — what the GAM
 * report card shows next to the raw CSV gross so the pipeline page ends at
 * the figure Culture Genesis actually splits with creators.
 */
export interface DsRunStats {
  totalMediaSales?: number;
  totalYouTubeFees?: number;
  totalChannelEarnings?: number;
}

export interface PipelineController {
  /** "YYYY-MM" — resolved from ?month=; '' while resolving on first mount */
  selectedMonth: string;
  selectMonth: (month: string) => void;
  pipeline: PipelineRun | null;
  uploadMeta: GamReportUploadMeta | null;
  /** GAM reports the month is expected to have (Admin → Settings; 1 by default) */
  expectedReports: number;
  /** Stats of the month's completed DS processing run (null until one exists) */
  dsRunStats: DsRunStats | null;
  history: PipelineRun[];
  loading: boolean;
  error: string | null;
  /** Key of the action in flight (start/cancel/discard/validation/cmUpload/cmLock/payouts/mode/rerun:<step>/removeSource:<id>) */
  actionBusy: string | null;
  phase: PipelinePhase;
  start: (dryRun: boolean, autoAdvance: boolean, monthOverride?: string) => Promise<void>;
  cancelProcessing: () => Promise<void>;
  discardDryRun: () => Promise<void>;
  runValidation: () => Promise<void>;
  confirmUpload: (notes?: string) => Promise<void>;
  confirmLock: () => Promise<void>;
  generatePayouts: (targets?: PayoutTarget[]) => Promise<void>;
  rerunStage1: (step: PipelineRerunStep) => Promise<{ runId: string; message: string }>;
  setMode: (autoAdvance: boolean) => Promise<void>;
  /**
   * Remove one report from the month's set. Rejects with
   * GamUploadRequiresForceError (no error banner) when the pipeline already
   * progressed — confirm, then call again with force.
   */
  removeSource: (sourceId: string, force?: boolean) => Promise<void>;
  refresh: () => Promise<void>;
  onUploaded: (result: GamReportUploadResult) => void;
}

export default function usePipelineController(): PipelineController {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedMonth = searchParams.get('month') || '';

  const [pipeline, setPipeline] = useState<PipelineRun | null>(null);
  const [uploadMeta, setUploadMeta] = useState<GamReportUploadMeta | null>(null);
  const [expectedReports, setExpectedReports] = useState(1);
  const [dsRunStats, setDsRunStats] = useState<DsRunStats | null>(null);
  const [history, setHistory] = useState<PipelineRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState<string | null>(null);

  // Guards a slow fetch for month A landing after the user switched to month B.
  const fetchSeq = useRef(0);
  // Run stats are immutable once a run completes — fetch each runId once.
  const dsRunCache = useRef<{ runId: string; stats: DsRunStats | null } | null>(null);

  const selectMonth = useCallback(
    (month: string, replace = false) => {
      if (!month) return;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('month', month);
          return next;
        },
        { replace }
      );
    },
    [setSearchParams]
  );

  // First mount without ?month=: default to the current pipeline's month, else
  // the expected cycle month (previous calendar month).
  useEffect(() => {
    if (selectedMonth) return;
    let stale = false;
    (async () => {
      let month = getExpectedCycleMonth();
      try {
        const { pipeline: current } = await getPipelineStatus();
        if (current?.month) month = current.month;
      } catch {
        // fall back to the expected cycle month
      }
      if (!stale) selectMonth(month, true);
    })();
    return () => {
      stale = true;
    };
  }, [selectedMonth, selectMonth]);

  const fetchMonth = useCallback(
    async (opts: { showSpinner?: boolean } = {}) => {
      if (!selectedMonth) return;
      const seq = ++fetchSeq.current;
      if (opts.showSpinner) setLoading(true);
      try {
        const [pipelineRes, uploadRes] = await Promise.all([
          getPipelineStatus(selectedMonth).catch((err: any) => {
            if (err?.status === 404) return { pipeline: null };
            throw err;
          }),
          getGamReportUpload(selectedMonth).catch(() => ({ upload: null, expectedReports: undefined })),
        ]);
        if (seq !== fetchSeq.current) return;
        setPipeline(pipelineRes.pipeline);
        setUploadMeta(uploadRes.upload);
        if (typeof uploadRes.expectedReports === 'number') setExpectedReports(uploadRes.expectedReports);
        setError(null);

        // Post-processing totals for the GAM report card: fetch the completed
        // DS run's stats (once per runId — a completed run never changes).
        const dsStage = pipelineRes.pipeline?.stages?.dsProcessing;
        const runId = dsStage?.status === 'completed' ? dsStage.runId : undefined;
        if (!runId) {
          setDsRunStats(null);
        } else if (dsRunCache.current?.runId === runId) {
          setDsRunStats(dsRunCache.current.stats);
        } else {
          const stats: DsRunStats | null = await getProcessingRun(runId)
            .then((r: any) => (r?.run?.stats as DsRunStats) || null)
            .catch(() => null); // decorative — never surface as a page error
          if (seq !== fetchSeq.current) return;
          dsRunCache.current = { runId, stats };
          setDsRunStats(stats);
        }
      } catch (err: any) {
        if (seq !== fetchSeq.current) return;
        setError(err?.message || 'Failed to load pipeline');
      } finally {
        if (seq === fetchSeq.current) setLoading(false);
      }
    },
    [selectedMonth]
  );

  const fetchHistory = useCallback(async () => {
    try {
      const { pipelines } = await getPipelineHistory(12);
      setHistory(pipelines);
    } catch {
      // history is decorative — never surface as a page error
    }
  }, []);

  // Load on month change
  useEffect(() => {
    if (!selectedMonth) return;
    setPipeline(null);
    setUploadMeta(null);
    setExpectedReports(1);
    setDsRunStats(null);
    fetchMonth({ showSpinner: true });
  }, [selectedMonth, fetchMonth]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const hasRunning = useMemo(
    () => Boolean(pipeline && Object.values(pipeline.stages).some((s) => s?.status === 'running')),
    [pipeline]
  );

  // Poll: fast while running, slow heartbeat otherwise
  useEffect(() => {
    if (!selectedMonth) return;
    const interval = setInterval(() => fetchMonth(), hasRunning ? RUNNING_POLL_MS : IDLE_POLL_MS);
    return () => clearInterval(interval);
  }, [selectedMonth, hasRunning, fetchMonth]);

  // Refetch when the browser tab becomes visible again
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchMonth();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchMonth]);

  /** Wrap an action: busy key, error surface, optional pipeline write-back. */
  const runAction = useCallback(
    async (key: string, fn: () => Promise<PipelineRun | null | void>): Promise<void> => {
      setActionBusy(key);
      setError(null);
      try {
        const result = await fn();
        if (result !== undefined) setPipeline(result ?? null);
      } catch (err: any) {
        setError(err?.message || 'Action failed');
        throw err;
      } finally {
        setActionBusy(null);
      }
    },
    []
  );

  const start = useCallback(
    async (dryRun: boolean, autoAdvance: boolean, monthOverride?: string) => {
      const month = monthOverride || selectedMonth;
      await runAction('start', async () => {
        const { pipeline: p } = await startPipeline(month, dryRun, autoAdvance);
        return p;
      });
      fetchHistory();
    },
    [selectedMonth, runAction, fetchHistory]
  );

  const cancelProcessing = useCallback(async () => {
    await runAction('cancel', async () => {
      const { pipeline: p } = await cancelPipelineProcessing(selectedMonth);
      return p;
    });
  }, [selectedMonth, runAction]);

  const discardDryRun = useCallback(async () => {
    await runAction('discard', async () => {
      await discardPipeline(selectedMonth);
      return null;
    });
    fetchHistory();
  }, [selectedMonth, runAction, fetchHistory]);

  const runValidation = useCallback(async () => {
    await runAction('validation', async () => {
      await runValidationApi(selectedMonth); // storing POST — stamps the stage
      await fetchMonth();
    });
  }, [selectedMonth, runAction, fetchMonth]);

  const confirmUpload = useCallback(
    async (notes?: string) => {
      await runAction('cmUpload', async () => {
        const { pipeline: p } = await confirmCmUpload(selectedMonth, notes);
        return p;
      });
    },
    [selectedMonth, runAction]
  );

  const confirmLock = useCallback(async () => {
    await runAction('cmLock', async () => {
      const { pipeline: p } = await confirmCmLock(selectedMonth);
      return p;
    });
  }, [selectedMonth, runAction]);

  const generatePayouts = useCallback(
    async (targets?: PayoutTarget[]) => {
      await runAction(targets?.length ? `payouts:${targets.join(',')}` : 'payouts', async () => {
        const { pipeline: p } = await triggerPipelinePayouts(selectedMonth, targets);
        return p;
      });
    },
    [selectedMonth, runAction]
  );

  const rerunStage1 = useCallback(
    async (step: PipelineRerunStep): Promise<{ runId: string; message: string }> => {
      setActionBusy(`rerun:${step}`);
      setError(null);
      try {
        const res = await rerunPipelineStep(selectedMonth, step);
        await fetchMonth();
        return res;
      } catch (err: any) {
        setError(err?.message || 'Re-run failed');
        throw err;
      } finally {
        setActionBusy(null);
      }
    },
    [selectedMonth, fetchMonth]
  );

  const setMode = useCallback(
    async (autoAdvance: boolean) => {
      await runAction('mode', async () => {
        const { pipeline: p } = await setPipelineMode(selectedMonth, autoAdvance);
        return p;
      });
    },
    [selectedMonth, runAction]
  );

  const removeSource = useCallback(
    async (sourceId: string, force = false) => {
      setActionBusy(`removeSource:${sourceId}`);
      setError(null);
      try {
        const { upload } = await removeGamReportSource(selectedMonth, sourceId, {
          force,
          baseRevision: uploadMeta?.revision,
        });
        setUploadMeta(upload);
        await fetchMonth();
      } catch (err: any) {
        // The force guard is a question for the user, not a failure
        if (!(err instanceof GamUploadRequiresForceError)) {
          setError(err?.message || 'Could not remove the report');
          fetchMonth(); // a conflict / not-found means the set changed — show the current one
        }
        throw err;
      } finally {
        setActionBusy(null);
      }
    },
    [selectedMonth, uploadMeta, fetchMonth]
  );

  const refresh = useCallback(async () => {
    await fetchMonth();
  }, [fetchMonth]);

  const onUploaded = useCallback(
    (result: GamReportUploadResult) => {
      // The server-detected month wins: navigate there; the month-change effect
      // refetches pipeline + upload meta. Same month → just refresh.
      if (result.upload.month === selectedMonth) {
        setUploadMeta(result.upload);
        fetchMonth();
      } else {
        selectMonth(result.upload.month);
      }
    },
    [selectedMonth, selectMonth, fetchMonth]
  );

  const phase = useMemo(
    () => derivePipelinePhase(pipeline, uploadMeta, expectedReports),
    [pipeline, uploadMeta, expectedReports]
  );

  return {
    selectedMonth,
    selectMonth,
    pipeline,
    uploadMeta,
    expectedReports,
    dsRunStats,
    history,
    loading,
    error,
    actionBusy,
    phase,
    start,
    cancelProcessing,
    discardDryRun,
    runValidation,
    confirmUpload,
    confirmLock,
    generatePayouts,
    rerunStage1,
    setMode,
    removeSource,
    refresh,
    onUploaded,
  };
}
